// SportRatesEditor (BPG-4) — edit a DOWNLINE's per-API-game partnership % after
// creation. Sits next to PartnershipEditor (which handles the 🎰 Casino win/loss)
// to complete the 4-card partnership edit surface. Talks to the sport-aware route
// mounted on BOTH broker apps:
//   GET  /admin/cricket-report/subagent-rates/:agentId → { perSport:{cricket,football,tennis} }
//   POST /admin/cricket-report/subagent-rates/:agentId  { sport, profitSharePct } → { perSport }
// Scope + Golden Limit are enforced server-side (admin → any; agent → own direct
// downline; the value is clamped to the broker's own per-sport rate). If the server
// caps the value, the reloaded number shows the capped result with a note.

import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type PerSport = { cricket: number; football: number; tennis: number };
const SPORTS: { key: keyof PerSport; label: string }[] = [
  { key: 'cricket', label: '🏏 Cricket' },
  { key: 'football', label: '⚽ Football' },
  { key: 'tennis', label: '🎾 Tennis' },
];

export default function SportRatesEditor({
  agentId, readOnly = false, onSaved,
}: {
  agentId: number; readOnly?: boolean; onSaved?: () => void;
}) {
  const [perSport, setPerSport] = useState<PerSport | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await adminApi.get<{ data: { perSport: PerSport } }>(`/admin/cricket-report/subagent-rates/${agentId}`);
      const ps = (d as any).data?.perSport ?? (d as any).perSport;
      setPerSport(ps);
      setDraft({ cricket: String(ps.cricket ?? 0), football: String(ps.football ?? 0), tennis: String(ps.tennis ?? 0) });
    } catch (e) { setErr((e as ApiError).message || 'Failed to load sport rates.'); }
    finally { setLoading(false); }
  }, [agentId]);
  useEffect(() => { void load(); }, [load]);

  async function saveOne(sport: keyof PerSport) {
    if (readOnly || savingKey) return;
    const v = Number(draft[sport]);
    if (!Number.isFinite(v) || v < 0) return;
    setSavingKey(sport); setErr(null); setNote(null);
    try {
      const out = await adminApi.post<{ data: { perSport: PerSport } }>(`/admin/cricket-report/subagent-rates/${agentId}`, { sport, profitSharePct: v });
      const ps = (out as any).data?.perSport ?? (out as any).perSport;
      setPerSport(ps);
      const applied = Number(ps[sport]);
      setDraft((d) => ({ ...d, [sport]: String(applied) }));
      setNote(applied < v ? `Capped to ${applied}% — that's your own limit for this game.` : 'Saved ✓');
      onSaved?.();
    } catch (e) { setErr((e as ApiError).message || 'Failed to save.'); }
    finally { setSavingKey(null); }
  }

  if (loading) return <div className="text-[12.5px] text-arena-600 dark:text-neon-200/70">Loading sport rates…</div>;
  if (err && !perSport) return <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 p-2 text-[12px] text-rose-700 dark:text-rose-300">{err}</div>;

  return (
    <div className="space-y-2.5">
      <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white">🏏 ⚽ 🎾 Live-sport P&amp;L share (per game)</h3>
      <p className="text-[11px] text-arena-600 dark:text-neon-200/70">Their cut on each game. Always ≤ your own — if you type more it's capped automatically.</p>
      <div className="space-y-2">
        {SPORTS.map(({ key, label }) => {
          const dirty = perSport != null && Number(draft[key]) !== Number(perSport[key]);
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="w-24 text-[13px] font-semibold text-neutral-800 dark:text-neutral-100">{label}</span>
              <div className="relative">
                <input
                  type="number" min={0} max={100} step={0.25} disabled={readOnly || savingKey === key}
                  value={draft[key] ?? ''} onChange={(e) => { setDraft((d) => ({ ...d, [key]: e.target.value })); setNote(null); }}
                  className="w-24 rounded-lg border border-arena-300 dark:border-white/15 bg-white dark:bg-white/5 px-2.5 py-1.5 text-[14px] text-right font-mono text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-50"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 pointer-events-none">%</span>
              </div>
              {!readOnly && (
                <button type="button" disabled={!dirty || savingKey === key} onClick={() => void saveOne(key)}
                  className="rounded-lg bg-violet-600 px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-40 hover:bg-violet-700 transition-colors">
                  {savingKey === key ? '…' : 'Save'}
                </button>
              )}
              <span className="text-[10.5px] text-neutral-400">only {key}</span>
            </div>
          );
        })}
      </div>
      {note && <div className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-300">{note}</div>}
      {err && perSport && <div className="text-[12px] font-semibold text-rose-600 dark:text-rose-400">{err}</div>}
    </div>
  );
}
